import React, { useState, useRef, useEffect } from "react";
import { Grid, TextField, Typography, Paper, Button } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import EditIcon from "@material-ui/icons/Edit";
import CodeIcon from "@material-ui/icons/Code";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import { ValidationUtils } from "../utils/ValidationUtils";
import PatientDetailsForm from "../components/PatientDetailsForm";
import PlaceOfServiceSelector from "../components/PlaceOfServiceSelector";
import PrescriberSelector from "../components/PrescriberSelector";
import MedicationSelector from "../components/MedicationSelector";
import PharmacyDetails from "../components/PharmacyDetails";
import { buildNewRxPayload } from "../utils/PayloadBuilders";
import { S3_CONFIG } from "../config/UPMSConfig";
import { PREDEFINED_ADDRESSES } from "../config/addresses";
import { COLORS } from "../styles";
import { WorkflowCard, WorkflowSelectionHeader, OneMedicalPrescriptionField } from "../components";
import { useS3Operations, usePrescriptionForm } from "../hooks";
import { IPatientDetails, INewRxStepHandoff } from "../types/PrescriptionIngestionTypes";
import {
  StyledHeaderBox,
  ResponseDisplay,
  StyledActionButton,
  CustomerSearchSection,
  ResetWorkflowButton,
} from "../components";

interface IProps {
  data?: any;
  // Advances to the Ingest step, carrying the uploaded fileName plus the searched
  // customer's identifiers (when a customer was retrieved).
  onContinue?: (handoff: INewRxStepHandoff) => void;
}

enum WorkflowType {
  NONE = "NONE",
  MANUAL_ENTRY = "MANUAL_ENTRY",
  UPLOAD_JSON = "UPLOAD_JSON",
}

const UploadNewRxToS3Section: React.FC<IProps> = ({ onContinue }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType>(
    WorkflowType.NONE
  );
  const [jsonInput, setJsonInput] = useState("");
  const [validationError, setValidationError] = useState<string>("");
  const [customerSearchKey, setCustomerSearchKey] = useState(0);
  // When checked, the prescription is treated as a One Medical prescription:
  // reveals the Prescriber Order Number field and forces ClinicName to
  // "One Medical" in the payload.
  const [isOneMedical, setIsOneMedical] = useState(false);
  // Identifiers of the customer retrieved via the optional search, carried forward
  // by the Continue button (empty when patient details were entered manually).
  const [customerIds, setCustomerIds] = useState<{
    personId?: string;
    profileId?: string;
  }>({});

  const responseRef = useRef<HTMLDivElement>(null);
  const s3ResponseRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const workflowContentRef = useRef<HTMLDivElement>(null);
  const resetButtonRef = useRef<HTMLDivElement>(null);
  const patientFormRef = useRef<HTMLDivElement>(null);

  // Use our custom hooks
  const {
    isUploading,
    uploadResponse,
    error: s3Error,
    uploadToS3,
    reset: resetS3,
  } = useS3Operations();
  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    selectedPrescriber,
    selectedMedication,
    placeOfService,
    prescriberOrderNumber,
    fieldErrors,
    setPatientField,
    handlePrescriberChange,
    handleMedicationChange,
    handleMedicationFieldChange,
    handlePlaceOfServiceChange,
    setPrescriberOrderNumber,
    updateFieldErrors,
    resetForm,
  } = usePrescriptionForm();

  const error = s3Error || validationError;

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      setTimeout(() => {
        errorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [error]);

  // Scroll to reset button when upload completes
  useEffect(() => {
    if (uploadResponse && resetButtonRef.current) {
      setTimeout(() => {
        resetButtonRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500);
    }
  }, [uploadResponse]);

  const handleBackToWorkflowSelection = () => {
    // Reset all state
    resetS3();
    resetForm();
    setJsonInput("");
    setValidationError("");
    setIsOneMedical(false);
    setSelectedWorkflow(WorkflowType.NONE);
  };

  const handleSelectAddress = (address: any) => {
    setPatientField("addressLine1", address.addressLine1);
    setPatientField("addressLine2", address.addressLine2);
    setPatientField("city", address.city);
    setPatientField("state", address.stateName);
    setPatientField("zipCode", address.zipCode);
  };

  const buildPrescriptionData = () => {
    return buildNewRxPayload({
      patientDetails,
      prescriberDetails,
      medicationDetails,
      placeOfService,
      prescriberOrderNumber: isOneMedical ? prescriberOrderNumber : undefined,
      isOneMedical,
    });
  };

  const handleWorkflowSelection = (workflow: WorkflowType) => {
    setSelectedWorkflow(workflow);
    // Scroll to workflow content after a short delay to allow rendering
    setTimeout(() => {
      workflowContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleSubmit = async () => {
    if (selectedWorkflow === WorkflowType.UPLOAD_JSON) {
      // JSON upload workflow
      if (!jsonInput.trim()) {
        return;
      }

      try {
        const parsedJson = JSON.parse(jsonInput);
        await uploadToS3(parsedJson);

        setTimeout(() => {
          if (responseRef.current) {
            responseRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } catch (err) {
        // Error is already handled by the hook
      }
    } else {
      // Manual entry workflow
      const patientValidation = ValidationUtils.validatePatientDetails(patientDetails);
      const medicationValidation = ValidationUtils.validateMedicationDetails({
        daysSupply: medicationDetails.daysSupply,
        ndc: medicationDetails.ndc,
        sigText: medicationDetails.sigText,
        quantity: medicationDetails.quantity,
        writtenDate: medicationDetails.writtenDate,
      });

      // Combine field errors from both validations
      const combinedFieldErrors = {
        ...patientValidation.fieldErrors,
        ...medicationValidation.fieldErrors,
      };

      // One Medical prescriptions must carry the real OM order number — it is
      // matched against the prescription prescribed at One Medical, so it cannot
      // be auto-generated.
      const isOneMedicalPonMissing = isOneMedical && !prescriberOrderNumber.trim();
      if (isOneMedicalPonMissing) {
        combinedFieldErrors.prescriberOrderNumber =
          "Prescriber Order Number is required for One Medical prescriptions.";
      }

      updateFieldErrors(combinedFieldErrors);

      if (!patientValidation.isValid || !medicationValidation.isValid || isOneMedicalPonMissing) {
        // Get the first error field name
        const firstErrorField = Object.keys(combinedFieldErrors)[0];
        const errorMessages = Object.entries(combinedFieldErrors)
          .filter(([, msg]) => msg) // Only include fields with errors
          .map(([field, msg]) => `${field}: ${msg}`)
          .join(", ");

        setValidationError(`Please fix the following fields: ${errorMessages}`);

        // Scroll to the first error field
        setTimeout(() => {
          const firstErrorElement = document.querySelector(
            `[name="${firstErrorField}"]`
          );
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);

        return;
      }

      // Clear validation error if validation passes
      setValidationError("");

      try {
        const prescriptionData = buildPrescriptionData();
        await uploadToS3(prescriptionData);

        setTimeout(() => {
          if (s3ResponseRef.current) {
            s3ResponseRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } catch (err) {
        // Error is already handled by the hook
      }
    }
  };

  const renderWorkflowSelection = () => {
    return (
      <Grid container spacing={4} style={{ maxWidth: 900, marginTop: 8 }}>
        <Grid item xs={12}>
          <WorkflowSelectionHeader
            title="Choose Your Workflow"
            subtitle="Select how you'd like to upload a prescription to S3"
          />
        </Grid>

        <Grid item xs={12}>
          <Alert severity="info" style={{ marginBottom: 16 }}>
            <Typography
              variant="body2"
              style={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              NewRx Sequential Workflow
            </Typography>
            <Typography variant="body2">
              Step 1: Upload to S3 → Step 2: Ingest to UPMS. Complete this step
              first before ingesting.
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12} md={6}>
          <WorkflowCard
            icon={EditIcon}
            title="Manual Form Entry"
            description="• Fill in patient details manually<br />• Choose prescriber, medication & pharmacy<br />• Upload to S3"
            onClick={() => handleWorkflowSelection(WorkflowType.MANUAL_ENTRY)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <WorkflowCard
            icon={CodeIcon}
            title="Upload JSON"
            description="• Paste prescription JSON directly<br />• Full control over payload<br />• Upload to S3"
            onClick={() => handleWorkflowSelection(WorkflowType.UPLOAD_JSON)}
          />
        </Grid>
      </Grid>
    );
  };

  const renderJsonInput = () => {
    return (
      <div ref={workflowContentRef}>
        <Button
          variant="text"
          onClick={handleBackToWorkflowSelection}
          startIcon={<ArrowBackIcon />}
          style={{ color: "black", marginBottom: 16 }}
        >
          Back to Workflow Selection
        </Button>

        <StyledHeaderBox
          title="Upload NewRx JSON to S3"
          subtitle={`Upload destination: ${S3_CONFIG.getFullPath()}`}
          awsAccount="pillpack-erx-beta"
          color="#546e7a"
          icon="1"
          infoMessage={
            <>
              This workflow allows you to paste a NewRx prescription JSON
              payload directly and upload it to S3 storage. This is useful for
              testing custom payloads or when you already have the JSON data.
              After uploading, you can use the{" "}
              <strong>"Ingest NewRx to UPMS"</strong> operation to complete the
              workflow.
            </>
          }
        />

        <Paper
          elevation={1}
          style={{
            padding: 32,
            borderRadius: 8,
            backgroundColor: COLORS.white,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                multiline
                minRows={20}
                label="NewRx Prescription JSON"
                variant="filled"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                fullWidth
                placeholder='{"MessageType": "NewRx", ...}'
              />
            </Grid>

            <Grid item xs={12}>
              <StyledActionButton
                label="Upload JSON to S3 Bucket"
                loadingLabel="Uploading..."
                onClick={handleSubmit}
                loading={isUploading}
                disabled={!!uploadResponse}
                color={COLORS.orange}
                startIcon={<CloudUploadIcon />}
              />
            </Grid>
          </Grid>
        </Paper>

        {uploadResponse && (
          <>
            <div ref={responseRef}>
              <ResponseDisplay
                title="S3 Upload Response"
                data={uploadResponse}
                color={COLORS.iconGray}
                icon="✓"
              />
            </div>
            <Grid container style={{ marginTop: 16 }} ref={resetButtonRef}>
              <ResetWorkflowButton
                label="Start New Upload"
                onReset={() => {
                  resetS3();
                  setJsonInput("");
                  setCustomerSearchKey((prev) => prev + 1);
                  // Scroll to top
                  setTimeout(() => {
                    workflowContentRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 100);
                }}
                style={{ marginTop: 0 }}
              />
            </Grid>
          </>
        )}
      </div>
    );
  };

  const renderManualEntry = () => {
    return (
      <div ref={workflowContentRef}>
        <Button
          variant="text"
          onClick={handleBackToWorkflowSelection}
          startIcon={<ArrowBackIcon />}
          style={{ color: "black", marginBottom: 16 }}
        >
          Back to Workflow Selection
        </Button>

        {error && (
          <Grid item xs={12} style={{ marginTop: 24, marginBottom: 24 }}>
            <div ref={errorRef}>
              <Alert severity="error">
                <strong>Error:</strong> {error}
              </Alert>
            </div>
          </Grid>
        )}

        <StyledHeaderBox
          title="Upload NewRx to S3"
          subtitle={`Upload destination: ${S3_CONFIG.getFullPath()}`}
          awsAccount="pillpack-erx-beta"
          color="#546e7a"
          icon="1"
          infoMessage="This workflow uploads a new prescription to S3 storage. Fill in the patient details, select prescriber, medication, and pharmacy information below."
        />

        <Grid container spacing={3}>
          <CustomerSearchSection
            searchKey={customerSearchKey}
            onCustomerRetrieved={(details) => {
              // Update all patient fields at once
              Object.entries(details).forEach(([field, value]) => {
                setPatientField(
                  field as keyof IPatientDetails,
                  value as string
                );
              });
            }}
            onIdsRetrieved={setCustomerIds}
            scrollTargetRef={patientFormRef}
          />

          <div ref={patientFormRef}>
            <PatientDetailsForm
              patientDetails={patientDetails}
              onFieldChange={setPatientField}
              fieldErrors={fieldErrors}
              addresses={PREDEFINED_ADDRESSES}
              onAddressSelect={handleSelectAddress}
              showAddressDropdown={true}
            />
          </div>

          <PlaceOfServiceSelector
            placeOfService={placeOfService}
            onPlaceOfServiceChange={handlePlaceOfServiceChange}
          />

          <OneMedicalPrescriptionField
            isOneMedical={isOneMedical}
            onIsOneMedicalChange={setIsOneMedical}
            prescriberOrderNumber={prescriberOrderNumber}
            onPrescriberOrderNumberChange={setPrescriberOrderNumber}
            fieldErrors={fieldErrors}
          />

          <PrescriberSelector
            selectedPrescriber={selectedPrescriber}
            prescriberDetails={prescriberDetails}
            onPrescriberChange={handlePrescriberChange}
          />

          <MedicationSelector
            selectedMedication={selectedMedication}
            medicationDetails={medicationDetails}
            onMedicationChange={handleMedicationChange}
            onMedicationFieldChange={handleMedicationFieldChange}
            fieldErrors={fieldErrors}
          />

          <PharmacyDetails />

          <Grid item xs={12}>
            <StyledActionButton
              label="Upload NewRx to S3 Bucket"
              loadingLabel="Uploading..."
              startIcon={<CloudUploadIcon />}
              onClick={handleSubmit}
              loading={isUploading}
              disabled={!!uploadResponse}
              color={COLORS.orange}
            />
          </Grid>
        </Grid>

        {uploadResponse && (
          <ResponseDisplay
            title="S3 Upload Response"
            subtitle="Prescription successfully uploaded to S3"
            data={uploadResponse}
            color={COLORS.iconGray}
            icon="2"
            forwardRef={s3ResponseRef}
          />
        )}

        {uploadResponse && (
          <>
            <Grid item xs={12} style={{ marginTop: 24 }}>
              <Alert severity="info">
                <Typography
                  variant="body2"
                  style={{ fontWeight: "bold", marginBottom: 4 }}
                >
                  Next Step: Ingest to UPMS
                </Typography>
                <Typography variant="body2">
                  To complete the prescription workflow and optionally create a
                  customer, use the <strong>"Ingest NewRx to UPMS"</strong>{" "}
                  operation. Click <strong>Continue</strong> below to go there
                  with the fileName prefilled, or copy the{" "}
                  <strong>fileName</strong> value from the S3 Upload Response
                  above and paste it in the next step.
                </Typography>
              </Alert>
            </Grid>

            {onContinue && (
              <Grid item xs={12} style={{ marginTop: 8 }}>
                <StyledActionButton
                  label="Continue to Ingest NewRx to UPMS"
                  onClick={() =>
                    onContinue({
                      fileName:
                        uploadResponse.s3Key ||
                        uploadResponse.fileName ||
                        "",
                      personId: customerIds.personId,
                      profileId: customerIds.profileId,
                      drugDescription: medicationDetails.drugDescription,
                      ndc: medicationDetails.ndc,
                    })
                  }
                  color={COLORS.orange}
                  startIcon={<ArrowForwardIcon />}
                />
              </Grid>
            )}

            <div ref={resetButtonRef}>
              <ResetWorkflowButton
                label="Start New Upload"
                onReset={() => {
                  resetS3();
                  resetForm();
                  setValidationError("");
                  setIsOneMedical(false);
                  setCustomerSearchKey((prev) => prev + 1);
                  // Scroll to top
                  setTimeout(() => {
                    workflowContentRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 100);
                }}
                style={{ marginTop: 16 }}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      {isUploading && <LoadingSpinner />}

      {selectedWorkflow === WorkflowType.NONE && renderWorkflowSelection()}
      {selectedWorkflow === WorkflowType.UPLOAD_JSON && renderJsonInput()}
      {selectedWorkflow === WorkflowType.MANUAL_ENTRY && renderManualEntry()}
    </Grid>
  );
};

export default UploadNewRxToS3Section;
